import { useMemo, useState } from 'react';

import {
  ChakraHStack,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  UniversalSelect,
} from '@equip.health/ui';

import { dropdownMenuOptionsMapper } from '~/lib/util/inquiry.util';

const TOP_LEVEL_ID = 'void-treatment-episode-modal';
const VOID_REASON_ID = `${TOP_LEVEL_ID}__void-reason`;

export interface VoidTreatmentEpisodeModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (reason: EpisodeStatusReason) => void;
  statusReasons: EpisodeStatusReason[];
}

const VoidTreatmentEpisodeModal = ({
  isOpen,
  onCancel,
  onConfirm,
  statusReasons,
}: VoidTreatmentEpisodeModalProps) => {
  const [statusReason, setStatusReason] = useState<string>();

  const voidStatuses = useMemo(
    () =>
      (statusReasons || [])
        .filter((x) => x.status === 'VOID')
        .map((x) => x.label),
    [statusReasons],
  );

  const options = useMemo(
    () => dropdownMenuOptionsMapper(voidStatuses),
    [voidStatuses],
  );

  const option = useMemo(
    () => (options || []).find((x) => x.label === statusReason),
    [options, statusReason],
  );

  const handleConfirm = () => {
    onConfirm(statusReasons.find((x) => x.label === statusReason));
  };

  return (
    <ModalV2
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            leftIcon={FAIconName.timesRegular}
            onClick={onCancel}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__void`}
            isDisabled={!statusReason}
            leftIcon={FAIconName.checkRegular}
            onClick={handleConfirm}
          >
            Void episode
          </FireflyButton>
        </ChakraHStack>
      }
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={onCancel}
      title="Void treatment episode"
    >
      <InputGroupV2 id={VOID_REASON_ID} label="Void reason">
        <UniversalSelect
          id={VOID_REASON_ID}
          isSingleSelect
          modalId={TOP_LEVEL_ID}
          onChange={(v) => setStatusReason(v?.value)}
          options={options}
          value={option}
        />
      </InputGroupV2>
    </ModalV2>
  );
};

export default VoidTreatmentEpisodeModal;
